import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ClubCard, ClubCardSkeleton, RejectModal, RequestRow } from '@/components/organizer/club-components';
import { organizerSidebar } from '@/config/sidebarConfig.ts';
import { getAffiliationRequestsApi, getClubsApi, updateAffiliationRequestApi } from '@/lib/api/clubsApi';
import { getPageNumbers } from '@/lib/pagination';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { ClubAffiliationRequestStatus } from '@shared/enums/domain.enum';
import type { Club, ClubAffiliationRequest } from '@shared/types/entities';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, ChevronLeft, ChevronRight, Clock, Flag, MessageSquare, Search } from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import StatCard from '../../components/ui/StatCard';

// ─── Page ────────────────────────────────────────────────────────
type TabId = 'clubs' | 'requests';
type ActiveFilter = 'all' | 'active' | 'inactive';

const VALID_TABS = new Set<string>(['clubs', 'requests']);
const VALID_ACTIVE_FILTERS = new Set<string>(['all', 'active', 'inactive']);
const VALID_REQ_STATUSES = new Set<string>(Object.values(ClubAffiliationRequestStatus));

export default function OrganizerClubs() {
  const orgId = useAuthStore(s => s.user)?.organizationsAdmin[0]?.organizationId;
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // ─── URL params ───
  const rawTab = searchParams.get('tab') ?? 'clubs';
  const tab: TabId = VALID_TABS.has(rawTab) ? (rawTab as TabId) : 'clubs';

  const rawActiveFilter = searchParams.get('filter') ?? 'all';
  const activeFilter: ActiveFilter = VALID_ACTIVE_FILTERS.has(rawActiveFilter)
    ? (rawActiveFilter as ActiveFilter)
    : 'all';

  const search = searchParams.get('search') ?? '';

  const rawReqStatus = searchParams.get('reqStatus') ?? '';
  const reqStatusFilter: ClubAffiliationRequestStatus | '' = VALID_REQ_STATUSES.has(rawReqStatus)
    ? (rawReqStatus as ClubAffiliationRequestStatus)
    : '';

  const reqPage = Math.max(1, Number(searchParams.get('reqPage') ?? '1') || 1);

  // ─── Local input state for debounce ───
  const [searchInput, setSearchInput] = useState(search);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // ─── Reject modal (local state only — not persisted in URL) ───
  const [rejectTarget, setRejectTarget] = useState<ClubAffiliationRequest | null>(null);

  function updateParams(updates: Record<string, string | undefined>) {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === undefined || value === '') next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  }

  // ─── Clubs query ───
  const { data: clubsData, isLoading: clubsLoading } = useQuery({
    queryKey: queryKeys.clubs.list({ organizationId: orgId, limit: 100 }),
    queryFn: () => getClubsApi({ organizationId: orgId, limit: 100 }),
    enabled: !!orgId,
    select: res => res.data,
  });

  const allClubs: Club[] = useMemo(() => clubsData ?? [], [clubsData]);

  // Clubs stats
  const totalClubs = allClubs.length;
  const activeClubs = allClubs.filter(c => c.isActive).length;

  // Clubs filtered
  const filteredClubs = useMemo(() => {
    let clubs = allClubs;
    if (activeFilter === 'active') clubs = clubs.filter(c => c.isActive);
    if (activeFilter === 'inactive') clubs = clubs.filter(c => !c.isActive);
    if (search) {
      const q = search.toLowerCase();
      clubs = clubs.filter(
        c =>
          c.name.toLowerCase().includes(q) ||
          (c.city ?? '').toLowerCase().includes(q) ||
          (c.department ?? '').toLowerCase().includes(q) ||
          (c.alias ?? '').toLowerCase().includes(q),
      );
    }
    return clubs;
  }, [allClubs, activeFilter, search]);

  // ─── Requests query ───
  const reqParams = {
    organizationId: orgId,
    ...(reqStatusFilter && { status: reqStatusFilter }),
    page: reqPage,
    limit: 15,
  };

  const { data: reqData, isLoading: reqLoading } = useQuery({
    queryKey: ['affiliation-requests', reqParams],
    queryFn: () => getAffiliationRequestsApi(reqParams),
    enabled: !!orgId,
    placeholderData: keepPreviousData,
  });

  const requests: ClubAffiliationRequest[] = reqData?.data ?? [];
  const reqTotal = reqData?.meta?.total ?? 0;
  const reqTotalPages = reqData?.meta?.totalPages ?? 1;

  // Pending count for tab badge
  const { data: pendingData } = useQuery({
    queryKey: ['affiliation-requests', { organizationId: orgId, status: ClubAffiliationRequestStatus.PENDING }],
    queryFn: () =>
      getAffiliationRequestsApi({
        organizationId: orgId,
        status: ClubAffiliationRequestStatus.PENDING,
        limit: 1,
      }),
    enabled: !!orgId,
  });
  const pendingCount = pendingData?.meta?.total ?? 0;

  // ─── Mutation: accept / reject ───
  const processMutation = useMutation({
    mutationFn: ({
      requestId,
      status,
      rejectedReason,
    }: {
      requestId: number;
      status: ClubAffiliationRequestStatus.ACCEPTED | ClubAffiliationRequestStatus.REJECTED;
      rejectedReason?: string;
    }) => updateAffiliationRequestApi(requestId, { status, rejectedReason }),
    onSuccess: (_, variables) => {
      const label = variables.status === ClubAffiliationRequestStatus.ACCEPTED ? 'Demande acceptée' : 'Demande refusée';
      toast.success(label);
      setRejectTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['affiliation-requests'] });
      if (variables.status === ClubAffiliationRequestStatus.ACCEPTED) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all() });
      }
    },
    onError: () => toast.error('Impossible de traiter la demande'),
  });

  // ─── Handlers ───
  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateParams({ search: value || undefined });
    }, 350);
  }

  function handleTabChange(newTab: TabId) {
    updateParams({ tab: newTab === 'clubs' ? undefined : newTab });
  }

  function handleActiveFilterChange(f: ActiveFilter) {
    updateParams({ filter: f === 'all' ? undefined : f });
  }

  function handleReqStatusChange(value: ClubAffiliationRequestStatus | '') {
    updateParams({ reqStatus: value || undefined, reqPage: undefined });
  }

  function handleReqPageChange(p: number) {
    updateParams({ reqPage: p === 1 ? undefined : String(p) });
  }

  return (
    <DashboardLayout sidebarConfig={organizerSidebar}>
      <div className='p-8'>
        <PageHeader title='Clubs affiliés' subtitle='Clubs partenaires de votre organisation' />

        {/* ─── Stats ── */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
          <StatCard
            icon={Flag}
            iconBg='bg-blue-100'
            iconColor='text-blue-600'
            label='Total clubs'
            value={clubsLoading ? '…' : String(totalClubs)}
            subtitle='Affiliés à votre organisation'
          />
          <StatCard
            icon={CheckCircle}
            iconBg='bg-emerald-100'
            iconColor='text-emerald-600'
            label='Actifs'
            value={clubsLoading ? '…' : String(activeClubs)}
            subtitle={totalClubs > 0 ? `${Math.round((activeClubs / totalClubs) * 100)} % des clubs` : ''}
          />
          <StatCard
            icon={Clock}
            iconBg='bg-amber-100'
            iconColor='text-amber-600'
            label='Demandes en attente'
            value={String(pendingCount)}
            subtitle='À traiter'
          />
        </div>

        {/* ─── Tabs ── */}
        <div className='flex border-b border-slate-200 mb-6'>
          <button
            onClick={() => handleTabChange('clubs')}
            className={`px-6 py-3 text-sm font-semibold transition border-b-2 ${
              tab === 'clubs'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Clubs affiliés
            {totalClubs > 0 && (
              <span className='ml-2 text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5'>{totalClubs}</span>
            )}
          </button>
          <button
            onClick={() => handleTabChange('requests')}
            className={`px-6 py-3 text-sm font-semibold transition border-b-2 flex items-center gap-2 ${
              tab === 'requests'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Demandes d&apos;affiliation
            {pendingCount > 0 && (
              <span className='inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-amber-500 text-white rounded-full'>
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
          </button>
        </div>

        {/* ═══════════ TAB CLUBS ═══════════ */}
        {tab === 'clubs' && (
          <>
            {/* Filters */}
            <div className='flex flex-wrap items-center gap-3 mb-6'>
              <div className='relative flex-1 min-w-[200px] max-w-sm'>
                <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
                <input
                  type='text'
                  value={searchInput}
                  onChange={e => handleSearchChange(e.target.value)}
                  placeholder='Rechercher un club…'
                  className='w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm'
                />
              </div>
              <div className='flex rounded-lg border border-slate-200 overflow-hidden bg-white'>
                {(['all', 'active', 'inactive'] as ActiveFilter[]).map(f => {
                  const labels = { all: 'Tous', active: 'Actifs', inactive: 'Inactifs' };
                  return (
                    <button
                      key={f}
                      onClick={() => handleActiveFilterChange(f)}
                      className={`px-4 py-2.5 text-sm font-medium transition border-r last:border-r-0 border-slate-200 ${
                        activeFilter === f ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {labels[f]}
                    </button>
                  );
                })}
              </div>
              {filteredClubs.length !== allClubs.length && (
                <span className='text-sm text-slate-400'>
                  {filteredClubs.length} résultat{filteredClubs.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Grid */}
            {clubsLoading ? (
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                {Array.from({ length: 6 }).map((_, i) => (
                  <ClubCardSkeleton key={i} />
                ))}
              </div>
            ) : filteredClubs.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-20 text-slate-400 gap-3'>
                <Flag className='w-12 h-12 opacity-30' />
                <p className='text-sm font-medium'>
                  {allClubs.length === 0
                    ? 'Aucun club affilié à votre organisation'
                    : 'Aucun club ne correspond aux filtres'}
                </p>
              </div>
            ) : (
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                {filteredClubs.map(club => (
                  <ClubCard key={club.id} club={club} />
                ))}
              </div>
            )}
          </>
        )}

        {/* ═══════════ TAB REQUESTS ═══════════ */}
        {tab === 'requests' && (
          <>
            {/* Filter */}
            <div className='flex items-center gap-3 mb-4'>
              <div className='flex rounded-lg border border-slate-200 overflow-hidden bg-white'>
                {(
                  [
                    { label: 'Toutes', value: '' },
                    { label: 'En attente', value: ClubAffiliationRequestStatus.PENDING },
                    { label: 'Acceptées', value: ClubAffiliationRequestStatus.ACCEPTED },
                    { label: 'Refusées', value: ClubAffiliationRequestStatus.REJECTED },
                  ] as { label: string; value: ClubAffiliationRequestStatus | '' }[]
                ).map(f => (
                  <button
                    key={f.value}
                    onClick={() => handleReqStatusChange(f.value)}
                    className={`px-4 py-2.5 text-sm font-medium transition border-r last:border-r-0 border-slate-200 ${
                      reqStatusFilter === f.value ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {reqTotal > 0 && (
                <span className='text-sm text-slate-400'>
                  {reqTotal} demande{reqTotal !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Table */}
            <div className='bg-white rounded-2xl border border-slate-200'>
              {/* Header */}
              <div className='grid grid-cols-12 gap-4 px-6 py-3 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider'>
                <div className='col-span-4'>Club demandeur</div>
                <div className='col-span-3'>Message</div>
                <div className='col-span-2'>Statut</div>
                <div className='col-span-1'>Date</div>
                <div className='col-span-2 text-right'>Actions</div>
              </div>

              {/* Body */}
              {reqLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className='grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-50'>
                    <div className='col-span-4 h-8 animate-pulse rounded bg-slate-200' />
                    <div className='col-span-3 h-4 animate-pulse rounded bg-slate-200' />
                    <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                    <div className='col-span-1 h-4 animate-pulse rounded bg-slate-200' />
                    <div className='col-span-2 h-8 animate-pulse rounded bg-slate-200' />
                  </div>
                ))
              ) : requests.length === 0 ? (
                <div className='flex flex-col items-center justify-center py-16 text-slate-400 gap-3'>
                  <MessageSquare className='w-10 h-10 opacity-30' />
                  <p className='text-sm font-medium'>Aucune demande d&apos;affiliation</p>
                </div>
              ) : (
                requests.map(req => (
                  <RequestRow
                    key={req.id}
                    request={req}
                    onAccept={id =>
                      processMutation.mutate({
                        requestId: id,
                        status: ClubAffiliationRequestStatus.ACCEPTED,
                      })
                    }
                    onReject={setRejectTarget}
                    accepting={processMutation.isPending}
                  />
                ))
              )}

              {/* Pagination */}
              {reqTotal > 0 && reqTotalPages > 1 && (
                <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
                  <p className='text-sm text-slate-500'>
                    {reqTotal} demande{reqTotal !== 1 ? 's' : ''} au total
                  </p>
                  <div className='flex items-center gap-1'>
                    <button
                      onClick={() => handleReqPageChange(Math.max(1, reqPage - 1))}
                      disabled={reqPage === 1}
                      aria-label='Page précédente'
                      className='rounded-lg border border-slate-200 p-2 hover:bg-slate-50 disabled:opacity-40 transition'
                    >
                      <ChevronLeft className='h-4 w-4' />
                    </button>
                    {getPageNumbers(reqPage, reqTotalPages).map((p, i) =>
                      p === '...' ? (
                        <span key={`d-${i}`} className='px-2 text-slate-400'>
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => handleReqPageChange(p as number)}
                          className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                            p === reqPage ? 'bg-blue-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {p}
                        </button>
                      ),
                    )}
                    <button
                      onClick={() => handleReqPageChange(Math.min(reqTotalPages, reqPage + 1))}
                      disabled={reqPage >= reqTotalPages}
                      aria-label='Page suivante'
                      className='rounded-lg border border-slate-200 p-2 hover:bg-slate-50 disabled:opacity-40 transition'
                    >
                      <ChevronRight className='h-4 w-4' />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Reject Modal */}
      <RejectModal
        request={rejectTarget}
        onClose={() => setRejectTarget(null)}
        loading={processMutation.isPending}
        onConfirm={reason => {
          if (!rejectTarget) return;
          processMutation.mutate({
            requestId: rejectTarget.id,
            status: ClubAffiliationRequestStatus.REJECTED,
            rejectedReason: reason || undefined,
          });
        }}
      />
    </DashboardLayout>
  );
}
